export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip Authorization header (login, refresh). */
  skipAuth?: boolean;
  /** Skip active Place header. */
  skipPlace?: boolean;
  /** Multipart body — skip JSON Content-Type. */
  formData?: boolean;
  /**
   * Operator toast when this call starts in a click, submit, or Enter.
   * Omitted mutating methods use the method default. `silent` skips the toast.
   * `error-only` skips the success toast but still surfaces failures (BR-WEB-22 —
   * create flows that navigate to the new record).
   * Calls from effects, debounce, and autosave stay quiet.
   */
  feedback?: 'created' | 'updated' | 'deleted' | 'silent' | 'error-only';
  /** Internal — prevents infinite 401 retry loops. */
  _retryOnUnauthorized?: boolean;
  /** Internal — gesture captured before the first await, kept across 401 retry. */
  _userInitiated?: boolean;
}

export interface FieldValidationError {
  field: string;
  code: string;
  message: string;
}

export class HttpError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string | undefined;
  readonly fieldErrors: FieldValidationError[] | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(options: {
    status: number;
    code: string;
    message: string;
    correlationId?: string;
    fieldErrors?: FieldValidationError[];
    details?: Record<string, unknown>;
  }) {
    super(options.message);
    this.name = 'HttpError';
    this.status = options.status;
    this.code = options.code;
    this.correlationId = options.correlationId;
    this.fieldErrors = options.fieldErrors;
    this.details = options.details;
  }
}

export interface HttpClientContext {
  getAccessToken: () => string | null;
  getActivePlaceId: () => string | null;
  onUnauthorized: () => Promise<boolean>;
}

export const PLACE_ID_HEADER = 'X-ZR-Place-Id';
export const CORRELATION_ID_HEADER = 'X-Correlation-Id';
